import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { FormInput } from 'components';
import { ILoginContext } from 'context';
import { useLogin } from 'hooks';

import styles from './ForgotPasswordForm.module.scss';

export function ForgotPasswordForm() {
  const { t } = useTranslation('auth');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { onSendResetPassword, onResetPasswordCancel }: ILoginContext = useLogin();

  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);

  const schema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string().email(t('validationEmailInvalid')).required(t('validationEmailRequired')),
      }),
    [t],
  );

  const form = useForm<{ email: string }>({
    resolver: yupResolver(schema),
    defaultValues: {},
    mode: 'all',
  });

  const handleResetPassword = (): void => {
    if (form.formState.isValid) {
      onSendResetPassword(form.getValues().email)
        .then(() => {
          setIsEmailSent(true);
        })
        .catch((error) => {
          enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
        });
    }
  };

  return (
    <div className={styles.form}>
      <Typography variant={isMobile ? 'h2' : 'h1'} component={isMobile ? 'h2' : 'h1'}>
        {t('forgotPasswordTitle')}
      </Typography>

      {!isEmailSent && (
        <>
          <Typography variant='body2' component='span'>
            {t('forgotPasswordHelp')}
          </Typography>

          <Stack className={styles.row}>
            <Stack direction='row' alignItems='center'>
              <Typography component='p' variant='body2'>
                {t('emailLabel')} <span className={styles.asterisk}>*</span>
              </Typography>
            </Stack>
            <FormInput name='email' control={form.control} errorHelperClassName={styles.errorMessage} />
          </Stack>

          <Stack className={styles.row}>
            <Button
              variant='contained'
              onClick={handleResetPassword}
              disabled={!form.formState.isValid}
              className={styles.formButton}
              type='submit'
            >
              {t('resetPassword')}
            </Button>
          </Stack>
        </>
      )}

      {isEmailSent && (
        <Typography className={styles.isEmailSentText} variant='body2' component='span'>
          {t('forgotPasswordSent')} <b>support@hipaalink.net.</b>
        </Typography>
      )}

      <Button variant='text' onClick={onResetPasswordCancel}>
        {t('backToLogin')}
      </Button>
    </div>
  );
}
